import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import {
  WizardStepStatusEnum,
  type WizardStepperBadgeIcon,
  type WizardStepperItem,
  type WizardStepperView,
  type WizardStepStatus,
} from './wizard.types';

/** Statuswoord in de aria-label; een schermlezer ziet het vinkje/streepje/nummer niet. */
const STATUS_LABELS: Record<WizardStepStatus, string> = {
  [WizardStepStatusEnum.NotStarted]: 'niet gestart',
  [WizardStepStatusEnum.Incomplete]: 'onvolledig',
  [WizardStepStatusEnum.Complete]: 'volledig',
};

/**
 * Positional model: the badge encodes POSITION, not data, so it must not borrow the data words. A
 * step after the current one has not been reached — saying "niet gestart" would claim knowledge
 * about its contents that this model does not have.
 */
const POSITIONAL_LABELS: Record<WizardStepStatus, string> = {
  [WizardStepStatusEnum.NotStarted]: 'nog te doen',
  // Unreachable by construction — the positional branch only ever derives Complete or NotStarted.
  // Present to satisfy the Record type, not because anything can produce it.
  [WizardStepStatusEnum.Incomplete]: 'nog te doen',
  [WizardStepStatusEnum.Complete]: 'afgerond',
};

/**
 * Custom wizard-stepper header (Figma "Interactive wizard steps", node 2353:36776) that replaces
 * Angular Material's horizontal stepper header. A centered row of atoms (20px badge + label)
 * joined by 32px connector lines.
 *
 * The component renders two models, chosen by whether the host supplies `statuses`:
 *
 * 1. **Data-driven** (`statuses` non-empty) — the interactive/non-linear flow the Figma design
 *    specifies. Each step carries one of three statuses derived from what the user actually
 *    filled in, and `active` is a COLOUR OVERLAY on top of it that never replaces the glyph:
 *
 *    | status        | badge                                   | active badge          |
 *    |---------------|-----------------------------------------|-----------------------|
 *    | `not-started` | transparent, 1px ring, step number      | filled, step number   |
 *    | `incomplete`  | filled, dashed-ring glyph               | filled, dashed glyph  |
 *    | `complete`    | filled, check glyph                     | filled, check glyph   |
 *
 *    Nothing is ever locked: every step stays clickable regardless of status, and clicking one
 *    makes it active while the previously active step falls back to its own real status.
 *
 * 2. **Positional** (`statuses` empty) — the linear wizard and the read-only progress indicators
 *    (mobile, submitted-onboarding screen), unchanged: steps before the current one read as
 *    complete, the current one shows its number, later ones show a hollow ring. Reachability
 *    still gates clicks here, because a linear flow genuinely can lock a step.
 *
 * Connector lines are flat grey in BOTH models and never encode progress — with steps completable
 * in any order a coloured segment would misrepresent overall progress.
 *
 * Hover and keyboard focus draw a 1px frame around the whole step (Border/Secondary on hover,
 * Border/Dark on focus). Deliberately NOT an underline — note that the Figma CANVAS annotation
 * still reads "Hover shows an underline on the label"; the linked Notion engineering handover
 * supersedes it with "No underline on the text — the border is the only hover affordance". Follow
 * the handover, and do not re-add the underline when the canvas text says otherwise.
 *
 * Set `compact` (mobile) to drop the labels and tighten the connectors.
 */
@Component({
  selector: 'app-wizard-stepper',
  imports: [MatIconModule],
  template: `
    <!--
      role=list / listitem rather than a plain group: the aria-label replaces the button's text
      content, so the visible step number never reaches a screen reader. List semantics hand back
      "1 van 4" without stuffing the ordinal into every label.
    -->
    <div
      class="wizard-stepper"
      role="list"
      aria-label="Stappen"
      [class.wizard-stepper--compact]="compact()"
      [class.wizard-stepper--static]="!interactive()">
      @for (view of stepViews(); track view.index) {
        <!--
          Connector + step travel together in one flex item, so a connector always belongs to the
          step it leads into and can never be stranded at the END of a wrapped line. It does mean a
          wrapped line begins with a connector; the row is designed as a single line and the
          connector is decorative (aria-hidden), so that is the lesser artefact.
        -->
        <span class="wizard-stepper__cell" role="listitem">
          @if (!$first) {
            <span class="wizard-stepper__line" aria-hidden="true"></span>
          }
          <button
            type="button"
            class="wizard-stepper__step"
            [attr.data-cy]="'wizard-step-' + view.index"
            [attr.data-status]="view.status"
            [disabled]="!view.clickable"
            [attr.aria-current]="view.active ? 'step' : null"
            [attr.aria-label]="view.ariaLabel"
            (click)="onStepClick(view)">
            <span [class]="atomClass(view)">
              @switch (view.icon) {
                @case ('check') {
                  <mat-icon class="wizard-stepper__glyph icon-size-3" svgIcon="lucide:check" />
                }
                @case ('dashed') {
                  <mat-icon class="wizard-stepper__glyph icon-size-3" svgIcon="lucide:circle-dashed" />
                }
                @default {
                  {{ view.index + 1 }}
                }
              }
            </span>
            @if (!compact()) {
              <span [class]="labelClass(view)">{{ view.label }}</span>
            }
          </button>
        </span>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;

      /*
        Figma "Stepper" palette, light (node 2353:36776) and dark (node 2440:112687). Every value
        below is a design VARIABLE, not a sampled pixel — Stepper/Active, Stepper/Completed,
        Stepper/Coming up, Stepper/Icon, Border/Secondary and Border/Dark.

        Active is pinned to the design's own purple rather than to var(--fuse-primary): the theme
        primary is the slate palette (#475569), which renders the active step grey-blue and loses
        the one colour accent the header has. A tenant that wants its own brand here overrides
        --dd-stepper-active-bg / --dd-stepper-active-label; the design value is the default.
        Dark uses the design's Indigo 300 with an INVERTED (dark) glyph — the light purple goes
        muddy on the dark surface.
      */
      --dd-stepper-active-bg: #a12db6; /* Figma Stepper/Active */
      --dd-stepper-active-fg: #ffffff; /* Figma Stepper/Icon */
      --dd-stepper-active-label: #a12db6;
      /* Figma Stepper/Completed — the filled badge behind both the check and the dashed ring. */
      --dd-stepper-filled-bg: #000000;
      --dd-stepper-filled-fg: #ffffff;
      /* Figma Stepper/Coming up — deliberately BLACK, not grey: grey is reserved for the linear
         flow's genuinely locked steps, and a not-started step here must not read as disabled. */
      --dd-stepper-ring: #000000;
      /* Figma Foreground/Default/Primary, identical to Stepper/Completed. */
      --dd-stepper-label: #000000;
      /* Figma Border/Secondary — same value as the global --dd-border-secondary token; spelled out
         so the light and dark blocks stay symmetric and the component styles itself standalone. */
      --dd-stepper-line: #a9a9ad;
      --dd-stepper-frame-hover: #a9a9ad;
      --dd-stepper-frame-focus: #000000; /* Figma Border/Dark */
      /* Locked linear step. Figma ships no disabled node, but its annotation names the role and
         the system already ships this grey as Border/Secondary. Exempt from WCAG 1.4.3/1.4.11
         because it marks a genuinely inoperable control. */
      --dd-stepper-disabled: #a9a9ad;
    }

    :host-context(.dark) {
      --dd-stepper-active-bg: #9c9dff; /* Figma Stepper/Active, dark */
      --dd-stepper-active-fg: #000000; /* Figma Stepper/Icon, dark */
      --dd-stepper-active-label: #9c9dff;
      --dd-stepper-filled-bg: #ffffff; /* Figma Stepper/Completed, dark */
      --dd-stepper-filled-fg: #000000; /* Figma Stepper/Icon, dark */
      --dd-stepper-ring: #ffffff; /* Figma Stepper/Coming up, dark */
      --dd-stepper-label: #ffffff; /* Figma Foreground/Default/Primary, dark */
      --dd-stepper-line: #545559; /* Figma Border/Secondary, dark */
      --dd-stepper-frame-hover: #545559;
      --dd-stepper-frame-focus: #ffffff; /* Figma Border/Dark, dark */
      --dd-stepper-disabled: #545559; /* Figma Border/Secondary, dark */
    }

    /*
      Figma "Stepper": a centered row of step atoms joined by connectors, 12px between each.

      Deliberately a single line. Wrapping put a dangling 32px connector at the head of the second
      line and knocked the two lines out of alignment; the design ships one row and the labels are
      nowrap, so overflow scrolls instead. The "safe center" keyword is what makes that survivable:
      plain centering plus overflow makes the leading steps unreachable (they overflow past the
      scroll origin), and "safe" falls back to flex-start exactly when that would happen. The plain
      center declaration above it is the fallback for engines without "safe".
    */
    .wizard-stepper {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: center;
      justify-content: safe center;
      gap: 12px;
      overflow-x: auto;
      /* The row is a header, not a scroll surface — the bar would sit under the step labels. */
      scrollbar-width: none;
    }

    .wizard-stepper::-webkit-scrollbar {
      display: none;
    }

    /* One flex item per step, carrying the connector that precedes it. Same 12px gap inside the
       cell as between cells, so the row rhythm is identical to a flat sibling list. */
    .wizard-stepper__cell {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 12px;
    }

    /* Connector between two steps: a flat 32px rule that never encodes progress. */
    .wizard-stepper__line {
      flex: 0 0 auto;
      width: 32px;
      height: 1px;
      background-color: var(--dd-stepper-line);
    }

    /* Halved for the compact mobile indicator. No Figma origin — the design file ships a single
       32px connector and no mobile stepper node; this is an engineering call, not a token. */
    .wizard-stepper--compact .wizard-stepper__line {
      width: 16px;
    }

    /*
      The step frame. Figma draws the 1px stroke INSIDE the 24px frame around a 20px badge, so the
      badge is inset 2px in total; in CSS the border sits outside the padding, hence 1px + 1px.
      The border is always present but transparent, so hover/focus only swap its colour — colouring
      a border that did not exist would shift the whole row by 2px on every hover.
    */
    .wizard-stepper__step {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 1px;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      font-family: inherit;
      color: var(--dd-stepper-label);
      cursor: pointer;
    }

    /*
      src/styles/components/_buttons.scss paints a grey wash behind ANY bare <button> on hover at
      specificity (0,2,1) — with no disabled or display-only guard. The design says the frame is the
      only hover affordance, so the background is pinned transparent in every state at a specificity
      that outranks it.
    */
    .wizard-stepper .wizard-stepper__step,
    .wizard-stepper .wizard-stepper__step:hover {
      background: transparent;
    }

    /* Hover: a grey frame around badge + label together. Explicitly NOT an underline. */
    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:not(:disabled):hover {
      border-color: var(--dd-stepper-frame-hover);
    }

    /*
      Keyboard focus: the same frame, but in the foreground colour so it is distinguishable from
      mouse hover. It has to be a border rather than an outline because the base layer kills the UA
      focus ring globally ("* { &:focus { outline: none !important } }" in tailwind.scss). Applies
      to the active step too — a user can tab to and re-select the step they are already on.

      Selector deliberately mirrors the hover rule and follows it: a plain
      .wizard-stepper__step:focus-visible is (0,2,0) and loses to hover's (0,5,0), which made the
      focus frame silently render in the hover grey whenever the pointer happened to rest on the
      focused step — exactly the case the two colours exist to tell apart.
    */
    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:not(:disabled):focus-visible {
      border-color: var(--dd-stepper-frame-focus);
    }

    /*
      Forced-colors (Windows high contrast) replaces every author colour, so the border-based focus
      frame becomes indistinguishable from the hover one. Fall back to a real outline there.
    */
    @media (forced-colors: active) {
      /* !important is load-bearing: the base layer's "* { &:focus { outline: none !important } }"
         (src/@fuse/styles/tailwind.scss) beats an author-normal declaration regardless of
         specificity, and :focus-visible implies :focus — without it this rule is dead code. */
      .wizard-stepper__step:focus-visible {
        outline: 2px solid Highlight !important;
        outline-offset: 1px;
      }

      /* Forced colours replace both background-color and color, so every badge would collapse to
         Canvas + CanvasText: the glyphs still separate the three statuses, but active and locked
         would stop being states at all. Re-express them in system colours, which forced-colors
         keeps. */
      .wizard-stepper .wizard-stepper__atom--active {
        background-color: Highlight;
        border-color: Highlight;
        color: HighlightText;
      }

      /* One line per selector — see the shim note on the locked-step rules below. */
      .wizard-stepper .wizard-stepper__step:disabled .wizard-stepper__atom:not(.wizard-stepper__atom--active) {
        border-color: GrayText;
        color: GrayText;
      }

      .wizard-stepper .wizard-stepper__step:disabled .wizard-stepper__label:not(.wizard-stepper__label--active) {
        color: GrayText;
      }
    }

    .wizard-stepper__step:disabled {
      cursor: not-allowed;
    }

    /* Display-only mode (mobile progress indicator, submitted onboarding): no affordance at all. */
    .wizard-stepper--static .wizard-stepper__step,
    .wizard-stepper--static .wizard-stepper__step:disabled {
      cursor: default;
    }

    .wizard-stepper__atom {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      width: 20px;
      height: 20px;
      /* Figma Border width/w-100. The border is on every atom, transparent unless the status paints
         it, so a filled and a hollow badge occupy the exact same 20px box. */
      border: 1px solid transparent;
      border-radius: 9999px;
      /* Figma Lable/Small/140 — the step number. */
      font-size: 12px;
      font-weight: 500;
      line-height: 1.4;
    }

    /*
      Atom / label colours are scoped under .wizard-stepper so they outrank the global
      button:disabled text colour. An unreachable or display-only step is communicated by the
      cursor, not by washing the atom out to the disabled grey (which is what made the rings read
      as faint and off-colour).
    */

    /*
      Every colour declaration below is !important, and it has to be. Each step is a bare button and
      src/@fuse/styles/tailwind.scss:84 ships "[disabled] * { @apply text-disabled !important }" —
      an author !important that no specificity can outrank. It expands to TWO declarations, color
      and --tw-text-opacity; only the colour one matters here, and background-color / border-color
      are genuinely untouched, which is why those are left at normal priority.

      It bites in two places: a locked linear step, and the display-only variant, which disables
      EVERY step to make it inert and would otherwise render the whole indicator in Fuse's disabled
      grey instead of the design colours. The values are unchanged; only the priority is.

      This is out-prioritising a symptom. The root cause is that an inert progress read-out is
      rendered as disabled <button> elements at all; swapping those for non-button markup and the
      locked linear step for aria-disabled would delete every !important below. Deliberately not
      done here — it also moves the locked step back into the tab order, which is a behaviour change
      that wants its own review.
    */

    /* Not started: hollow circle with a full-strength ring and the step number. Plain CSS border
       colour (not a Tailwind border util) dodges the global ".dark *" border-opacity clamp so it
       keeps full strength in dark mode. */
    .wizard-stepper .wizard-stepper__atom--not-started {
      background-color: transparent;
      border-color: var(--dd-stepper-ring);
      color: var(--dd-stepper-ring) !important;
    }

    /* Incomplete + complete share the filled badge; only the glyph inside differs. */
    .wizard-stepper .wizard-stepper__atom--incomplete,
    .wizard-stepper .wizard-stepper__atom--complete {
      background-color: var(--dd-stepper-filled-bg);
      border-color: var(--dd-stepper-filled-bg);
      color: var(--dd-stepper-filled-fg) !important;
    }

    /* Active is a colour overlay on whichever status the step really has, so it must come last. */
    .wizard-stepper .wizard-stepper__atom--active {
      background-color: var(--dd-stepper-active-bg);
      border-color: var(--dd-stepper-active-bg);
      color: var(--dd-stepper-active-fg) !important;
    }

    /*
      A genuinely locked step — LINEAR FLOW ONLY. This is the one place the design reserves grey
      for: "grey is reserved for the linear flow's genuinely disabled state". Never reachable in the
      data-driven model, where nothing is ever locked, and deliberately excluded in display-only
      mode, which is a progress read-out rather than a lock. Until now a locked step was
      pixel-identical to a reachable one and only the cursor gave it away — after the click.
      The glyph is kept: a completed-but-now-unreachable step still reads as done, just muted.

      The :not(…--active) guards are load-bearing. Reachability is value-driven, so a value entered
      on step 3 can retro-lock step 1 and make everything after it unreachable — including the step
      the user is standing on. Without the guard these (0,5,0) rules overwrite the (0,2,0) active
      overlay and the header loses its only "you are here" marker, leaving a grey number on the
      purple fill at roughly 1.6:1. You are never locked out of the step you are already on; the
      lock only concerns steps you cannot navigate TO.

      The colour declarations need !important, and only they do. src/@fuse/styles/tailwind.scss:84
      ships "[disabled] * { @apply text-disabled !important }", which forces the Fuse disabled
      colour onto every descendant of a disabled element. An author !important beats any
      specificity, so without this the badge number and the label render in Fuse's grey while the
      ring and fill correctly render in Border/Secondary — two different greys in one badge.
    */
    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:disabled .wizard-stepper__atom--not-started:not(.wizard-stepper__atom--active) {
      border-color: var(--dd-stepper-disabled);
      color: var(--dd-stepper-disabled) !important;
    }

    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:disabled .wizard-stepper__atom--incomplete:not(.wizard-stepper__atom--active),
    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:disabled .wizard-stepper__atom--complete:not(.wizard-stepper__atom--active) {
      background-color: var(--dd-stepper-disabled);
      border-color: var(--dd-stepper-disabled);
      /* The glyph keeps the inverted colour: muting the fill already says "unavailable", muting the
         check as well would leave it unreadable on the grey. */
      color: var(--dd-stepper-filled-fg) !important;
    }

    .wizard-stepper:not(.wizard-stepper--static) .wizard-stepper__step:disabled .wizard-stepper__label:not(.wizard-stepper__label--active) {
      color: var(--dd-stepper-disabled) !important;
    }

    /* mat-icon does not inherit colour on its own; without this the glyph falls back to the
       theme's icon colour and disappears on the filled badge. */
    .wizard-stepper__glyph {
      width: 12px;
      height: 12px;
      /* !important for the same reason as the badge colours: inside a disabled step the global
         wash would repaint the glyph too, hiding a white check on a filled badge. */
      color: inherit !important;
    }

    .wizard-stepper .wizard-stepper__label {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
      white-space: nowrap;
      color: var(--dd-stepper-label) !important;
    }

    .wizard-stepper .wizard-stepper__label--active {
      color: var(--dd-stepper-active-label) !important;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WizardStepperComponent {
  /** Step labels in visual order (form steps first, an optional review step last). */
  readonly steps = input.required<WizardStepperItem[]>();

  /** Index of the currently active step. */
  readonly currentIndex = input.required<number>();

  /**
   * Per-step underlying status. Supplying it switches the header to the data-driven model from the
   * Figma design; leaving it empty keeps the positional model the linear wizard has always used.
   */
  readonly statuses = input<WizardStepStatus[]>([]);

  /** Per-step reachability; missing/short entries default to reachable. Positional model only. */
  readonly reachable = input<boolean[]>([]);

  /** Drop the labels and tighten the connectors — used as a compact mobile progress indicator. */
  readonly compact = input(false);

  /** Whether steps emit `stepClick`; false renders a display-only progress indicator. */
  readonly interactive = input(true);

  /** Emits the index of a clicked, clickable step. */
  readonly stepClick = output<number>();

  protected readonly stepViews = computed<WizardStepperView[]>(() => {
    const current = this.currentIndex();
    const reach = this.reachable();
    const statuses = this.statuses();
    const canNavigate = this.interactive();
    const dataDriven = statuses.length > 0;

    return this.steps().map((step, index) => {
      const active = index === current;
      const status: WizardStepStatus = dataDriven
        ? (statuses[index] ?? WizardStepStatusEnum.NotStarted)
        : index < current
          ? WizardStepStatusEnum.Complete
          : WizardStepStatusEnum.NotStarted;

      return {
        index,
        label: step.label,
        status,
        active,
        icon: this.#badgeIcon(status, active, dataDriven),
        /*
          Data-driven: nothing is ever locked. Positional: the linear flow's gating still applies,
          except to the step the user is standing on — you are never locked out of where you already
          are. Reachability is value-driven, so without that exemption a value entered on step 3
          could retro-lock step 1 and disable step 3 underneath the user, dropping it out of the tab
          order. The matching :not(...--active) CSS guards stay as belt-and-braces.
        */
        clickable: canNavigate && (dataDriven || active || (reach[index] ?? true)),
        ariaLabel: `${step.label}, ${this.#stateWord(status, active, dataDriven, canNavigate)}`,
      };
    });
  });

  /**
   * The word after the step name in the aria-label — a screen reader sees neither the glyph nor the
   * colour.
   *
   * Data-driven: always the REAL status, never "actief". Active is a colour overlay, not a fourth
   * status, and swapping it in would rob the one step the user is standing on of its status; being
   * current is carried structurally by `aria-current="step"`.
   *
   * Positional: the status is fabricated from the index, so the data words would lie — the active
   * step would announce "niet gestart" while the user is filling it in. Here "actief" IS the honest
   * answer, but ONLY while the stepper is interactive. A display-only indicator (the submitted
   * onboarding header) is a read-out where nobody is standing anywhere, so it falls back to the
   * position word, which is also what its badge actually renders.
   */
  #stateWord(status: WizardStepStatus, active: boolean, dataDriven: boolean, interactive: boolean): string {
    if (dataDriven) {
      return STATUS_LABELS[status];
    }
    return active && interactive ? 'actief' : POSITIONAL_LABELS[status];
  }

  /**
   * In the data-driven model the glyph follows the real status and `active` only recolours it. The
   * positional model keeps its old rule: the step you are standing on shows its number, so the
   * header still reads as "you are at 3 of 5" rather than as a completion summary.
   */
  #badgeIcon(status: WizardStepStatus, active: boolean, dataDriven: boolean): WizardStepperBadgeIcon {
    if (active && !dataDriven) {
      return 'number';
    }
    if (status === WizardStepStatusEnum.Complete) {
      return 'check';
    }
    return status === WizardStepStatusEnum.Incomplete ? 'dashed' : 'number';
  }

  protected atomClass(view: WizardStepperView): string {
    const active = view.active ? ' wizard-stepper__atom--active' : '';
    return `wizard-stepper__atom wizard-stepper__atom--${view.status}${active}`;
  }

  protected labelClass(view: WizardStepperView): string {
    return view.active ? 'wizard-stepper__label wizard-stepper__label--active' : 'wizard-stepper__label';
  }

  protected onStepClick(view: WizardStepperView): void {
    if (view.clickable) {
      this.stepClick.emit(view.index);
    }
  }
}
